import type { Group, Lead, Pipeline } from '@shared/types';
import { toE164 } from '@shared/phone';

/** Lead-date windows, all rolling: the last 24 hours, 7 × 24 or 30 × 24 hours — not "since midnight" or calendar weeks. */
export type Period = 'all' | '24h' | '7d' | '30d';
export type Filters = { q: string; stageName: string; source: string; owner: string; period: Period; pipelineId: string };

export const PERIOD_LABELS: Record<Period, string> = { all: 'הכל', '24h': '24 שעות', '7d': '7 ימים', '30d': '30 ימים' };

/** No filter at all. */
export const EMPTY_FILTERS: Filters = { q: '', stageName: '', source: '', owner: '', period: 'all', pipelineId: '' };
/** What the app opens with, and what "נקה" returns to: the last 30 days. */
export const DEFAULT_FILTERS: Filters = { ...EMPTY_FILTERS, period: '30d' };

export function periodStart(period: Period, now: number): number {
  if (period === 'all') return 0;
  return now - (period === '24h' ? 1 : period === '7d' ? 7 : 30) * 86_400_000;
}

/** The date filter alone — the KPI tiles follow it, but not the search or the other filters. */
export function inPeriod(lead: Lead, period: Period, now = Date.now()): boolean {
  const start = periodStart(period, now);
  return !start || (!!lead.leadDate && Date.parse(lead.leadDate) >= start);
}

export function normalizeQuery(q: string): { text: string; digits: string } {
  const text = q.trim().toLowerCase();
  return { text, digits: text.replace(/\D/g, '') };
}

/** Free-text search over name, phone (either format) and email. Runs client-side over the whole read model. */
export function matchesQuery(lead: Lead, nq: { text: string; digits: string }): boolean {
  if (!nq.text) return true;
  if (lead.fullName.toLowerCase().includes(nq.text)) return true;
  if (lead.email?.toLowerCase().includes(nq.text)) return true;
  if (nq.digits.length >= 3) {
    const raw = (lead.phone ?? '').replace(/\D/g, '');
    if (raw.includes(nq.digits)) return true;
    const e = toE164(lead.phone)?.replace(/\D/g, '') ?? '';
    if (e.includes(nq.digits)) return true;
    const local = e.startsWith('972') ? '0' + e.slice(3) : e;
    if (local.includes(nq.digits)) return true;
  }
  return false;
}

export function applyFilters(leads: Lead[], f: Filters, stageName: (id: string) => string, now = Date.now()): Lead[] {
  const nq = normalizeQuery(f.q);
  return leads.filter((l) => {
    if (f.pipelineId && l.pipelineId !== f.pipelineId) return false;
    if (f.stageName && stageName(l.stageId) !== f.stageName) return false;
    if (f.source && (l.source ?? '') !== f.source) return false;
    if (f.owner) {
      if (f.owner === '__none__' ? !!l.assignedTo : l.assignedTo !== f.owner) return false;
    }
    if (!inPeriod(l, f.period, now)) return false;
    return matchesQuery(l, nq);
  });
}

export type GroupedLeads = { group: Group; leads: Lead[] };

/** Groups are named sets of stage names; leads whose stage belongs to no group land in a trailing "אחר" bucket. */
export function groupLeads(leads: Lead[], groups: Group[], stageName: (id: string) => string): GroupedLeads[] {
  const byName = new Map<string, Lead[]>();
  for (const g of groups) byName.set(g.id, []);
  const other: Lead[] = [];
  const stageToGroup = new Map<string, string>();
  for (const g of groups) for (const s of g.stageNames) if (!stageToGroup.has(s)) stageToGroup.set(s, g.id);
  for (const l of leads) {
    const gid = stageToGroup.get(stageName(l.stageId));
    if (gid) byName.get(gid)!.push(l);
    else other.push(l);
  }
  const out: GroupedLeads[] = groups.map((g) => ({ group: g, leads: byName.get(g.id)! }));
  if (other.length) out.push({ group: { id: '__other__', name: 'אחר', stageNames: [], sortOrder: 999 }, leads: other });
  return out;
}

export function distinctSources(leads: Lead[]): string[] {
  const c = new Map<string, number>();
  for (const l of leads) if (l.source) c.set(l.source, (c.get(l.source) ?? 0) + 1);
  return [...c.entries()].sort((a, b) => b[1] - a[1]).map((e) => e[0]);
}

export function sortByDateDesc(leads: Lead[]): Lead[] {
  return [...leads].sort((a, b) => (b.leadDate ? Date.parse(b.leadDate) : 0) - (a.leadDate ? Date.parse(a.leadDate) : 0));
}

export function pipelineOf(pipelines: Pipeline[], id: string): Pipeline | undefined {
  return pipelines.find((p) => p.id === id);
}
